"use client";
import React, { useCallback, useState } from "react";
import { fetchEntitiesAll, fetchEntity } from "~/services";
import { TurnrGrid } from "~/components/TurnrGrid.jsx";
import Head from "next/head";
import ReturnCard from "./ReturnCard";
import ReturnRefundCard from "./ReturnRefundCard";
import { getOrderName } from "~/utils";
import { ENTITY_SCOPE, ENTITY_TYPE, WAREHOUSE_RETURN_STATUS } from "~/constants";
import { HandleWarehouseComplaintReloadButton } from "../.././warehouse-reports/././require-action/HandleWarehouseComplaintButton";
import { TurnrCard, TurnrCardContent, TurnrCardHeader } from "~/components/card/TurnrCard.jsx";
import { TurnrAlert, TurnrInfoAlert } from "~/components/alerts/TurnrAlert.jsx";
import { ServiceErrorAlert } from "~/components/Error.jsx";
import DotsMenu from "~/components/DotsMenu";
import EntityActionsDotsMenu from "~/components/entityAction/EntityActionsDotsMenu.jsx";
import CreateReturnModal from "../CreateReturnModal";
import { getServiceLoaderInOrder, serviceHasError, serviceIsLoadingOrNotFetched, useServiceHook } from "rylak-common";
import EntityAddressInfoBox from "~/components/infoBoxes/EntityAddressInfoBox.jsx";
import CustomerTagsChips from "~/components/chips/CustomerTagsChips.jsx";
import { useRunCallbackIfValueDeeplyChanged } from "rylak-common/src/hooks/useRunCallbackIfValueDeeplyChanged.jsx";
import OrderNotesCard from "./OrderNotesCard";
import { getPathParam, isRoleSuperAdmin } from "~/cores/appCore";
import ObjectAsJson from "~/components/ObjectAsJson.jsx";
import styles from "./index.module.scss";
import LineDivider from "rylak-common/src/components/divider/LineDivider";
import ReturnStatisticsCard from "~/components/charts/chartCommon/tooltips/ReturnStatisticsCard.jsx";
import TurnrOrderLink from "~/components/links/TurnrOrderLink.jsx";
import { useAppProps } from "~/app/_apps/AppPropsContext";
import OrderInformationInfoBox from "~/components/infoBoxes/OrderInformationInfoBox.jsx";
import { EntityActionStatusChip, EntityActionStatusChipDeprecated } from "~/components/entityAction/EntityActionStatusChip.jsx";
import RefundStatusChip from "~/components/chips/statusChips/RefundStatusChip.jsx";
import { RENDER_TYPE } from "~/components/entityAction/utils/entityActionsCore.js";
import WarehouseReturnStatusChip from "~/components/chips/statusChips/WarehouseReturnStatusChip.jsx";

function PageWrapper({ children, onlyOneReturnCard }) {
    return (
        <TurnrGrid style={{ maxWidth: onlyOneReturnCard && "1400px" }} container direction={"row"} spacing={2} xs={12}>
            {children}
        </TurnrGrid>
    );
}

function ReturnCardsWrapper({ children, onlyOneReturnCard }) {
    return (
        <TurnrGrid container item spacing={2} direction={"row"} xs={12} lg={onlyOneReturnCard ? 7 : 8} xl={onlyOneReturnCard ? 8 : 9} style={{ alignSelf: "flex-start" }}>
            {children}
        </TurnrGrid>
    );
}

function OrderCardsWrapper({ children, onlyOneReturnCard }) {
    return (
        <TurnrGrid item style={{ alignSelf: "flex-start" }} xs={12} lg={onlyOneReturnCard ? 5 : 4} xl={onlyOneReturnCard ? 4 : 3}>
            {children}
        </TurnrGrid>
    );
}

export default function ReturnPage() {
    const { appProps } = useAppProps();
    const orderId = getPathParam(appProps, { key: "id" });

    let { serviceData: orderService, fetchServiceData: triggerOrderFetch } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchData: {
            entityType: ENTITY_TYPE.ORDER,
        },
        fetchFn: fetchEntity,
    });
    let { serviceData: returnsService, fetchServiceData: triggerReturnsFetch } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchData: {
            entityType: ENTITY_TYPE.RETURN,
        },
        fetchFn: fetchEntitiesAll,
    });
    let { serviceData: refundTransactionsService, fetchServiceData: triggerRefundTransactionsFetch } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchData: {
            entityType: ENTITY_TYPE.REFUND_TRANSACTION,
            entityScope: ENTITY_SCOPE.FULL,
        },
        fetchFn: fetchEntitiesAll,
    });
    let { serviceData: warehouseReturnsService, fetchServiceData: triggerWarehouseReturnsFetch } = useServiceHook({
        appProps,
        fetchRequested: false,
        fetchData: {
            entityType: ENTITY_TYPE.WAREHOUSE_RETURN,
            entityScope: ENTITY_SCOPE.FULL,
        },
        fetchFn: fetchEntitiesAll,
    });

    const { isServiceLoadingInOrder } = getServiceLoaderInOrder(orderService, returnsService, refundTransactionsService, warehouseReturnsService);

    const reloadData = useCallback(() => {
        triggerOrderFetch({ entityId: orderId }, { silentFetch: true });
        triggerReturnsFetch({ orderId: orderId }, { silentFetch: true });
        triggerRefundTransactionsFetch({ orderId: orderId }, { silentFetch: true });
        triggerWarehouseReturnsFetch({ orderId: orderId }, { silentFetch: true });
    }, [orderId, triggerOrderFetch, triggerReturnsFetch, triggerRefundTransactionsFetch, triggerWarehouseReturnsFetch]);
    useRunCallbackIfValueDeeplyChanged(orderId, reloadData);

    const warehouseManualChecks = warehouseReturnsService?.data?.filter((warehouseReturn) => warehouseReturn?.warehouseReturnStatus === WAREHOUSE_RETURN_STATUS.AWAITING_MERCHANT_CHECK);

    const order = orderService?.data;
    const returns = returnsService?.data;
    const refundTransactions = refundTransactionsService?.data;
    const manualRefundTransactions = refundTransactions?.filter((refundTransaction) => !refundTransaction?.orderReturn);

    const loading = serviceIsLoadingOrNotFetched(orderService, returnsService, refundTransactionsService, warehouseReturnsService);

    const [createReturnModalOpen, setCreateReturnModalOpen] = useState(false);

    const onlyOneReturnCard = serviceIsLoadingOrNotFetched(returnsService) || returns?.length === 1;

    const parentOrder = order?.parentOrder;

    const orderErrorChip = order?.workerInfo?.error ? (
        <EntityActionStatusChipDeprecated appProps={appProps} statusData={{}} entity={order} entityType={ENTITY_TYPE.ORDER} onSuccess={reloadData} />
    ) : order?.refund?.workerInfo?.error ? (
        <RefundStatusChip appProps={appProps} order={order} onSuccess={reloadData} />
    ) : null;

    return (
        <>
            <Head>
                <title>Order {getOrderName(order)}</title>
            </Head>
            <CreateReturnModal open={createReturnModalOpen} onSuccess={reloadData} onClose={setCreateReturnModalOpen} appProps={appProps} order={order} />
            <PageWrapper onlyOneReturnCard={onlyOneReturnCard}>
                <ServiceErrorAlert fullWidth={false} serviceData={warehouseReturnsService} />
                {parentOrder && (
                    <TurnrGrid container item>
                        <TurnrGrid item>
                            <TurnrInfoAlert showIcon={false}>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                        fontSize: "14px",
                                    }}
                                >
                                    This order was made as an exchange on order: <TurnrOrderLink order={order?.parentOrder} />
                                </div>
                            </TurnrInfoAlert>
                        </TurnrGrid>
                    </TurnrGrid>
                )}
                <TurnrGrid container item spacing={3}>
                    {serviceHasError(orderService) ? (
                        <ServiceErrorAlert serviceData={orderService} fullWidth={false} />
                    ) : (
                        <>
                            <ReturnCardsWrapper onlyOneReturnCard={onlyOneReturnCard}>
                                {warehouseManualChecks?.length > 0 && (
                                    <TurnrGrid container item spacing={2} direction={"row"} xs={12} style={{ alignSelf: "flex-start" }}>
                                        <TurnrGrid item xs={12}>
                                            <TurnrGrid container spacing={2}>
                                                {warehouseManualChecks?.map((warehouseReturn, index) => {
                                                    if (warehouseReturn?.statusAction?.frontendSpecification?.renderType === RENDER_TYPE.WAREHOUSE_RETURN_HANDLE_COMPLAINT) {
                                                        return (
                                                            <TurnrGrid item xs={12} key={index}>
                                                                <TurnrAlert fullWidth={true} title={"You have unhandled warehouse reports"}>
                                                                    <HandleWarehouseComplaintReloadButton appProps={appProps} warehouseReport={warehouseReturn} />
                                                                </TurnrAlert>
                                                            </TurnrGrid>
                                                        );
                                                    } else {
                                                        return (
                                                            <TurnrGrid item xs={12} key={index}>
                                                                <TurnrAlert fullWidth={true} title={"You have unhandled warehouse reports"}>
                                                                    <EntityActionStatusChip
                                                                        size="small"
                                                                        appProps={appProps}
                                                                        entity={warehouseReturn}
                                                                        onSuccess={reloadData}
                                                                        StatusChipComponent={WarehouseReturnStatusChip}
                                                                    />
                                                                </TurnrAlert>
                                                            </TurnrGrid>
                                                        );
                                                    }
                                                })}
                                            </TurnrGrid>
                                        </TurnrGrid>
                                    </TurnrGrid>
                                )}
                                <TurnrGrid container item xs={12} spacing={3}>
                                    {isServiceLoadingInOrder(returnsService) || serviceHasError(returnsService) ? (
                                        <TurnrGrid item xs={12} xl={onlyOneReturnCard ? 12 : 6}>
                                            {isServiceLoadingInOrder(returnsService) && <ReturnCard appProps={appProps} refundTransactionsService={refundTransactionsService} />}
                                            <ServiceErrorAlert noMargins serviceData={returnsService} fullWidth={true} />
                                        </TurnrGrid>
                                    ) : (
                                        returns
                                            ?.sort((a, b) => {
                                                if (a?.id < b?.id) {
                                                    return -1;
                                                }
                                                return 1;
                                            })
                                            .map((_, index) => {
                                                const i = returns.length - 1 - index;
                                                const r = returns[i];
                                                return (
                                                    <TurnrGrid key={r?.id} item xs={12} xl={onlyOneReturnCard ? 12 : 6}>
                                                        <div
                                                            style={{
                                                                width: "100%",
                                                            }}
                                                        >
                                                            <ReturnCard
                                                                appProps={appProps}
                                                                index={i}
                                                                orderReturn={r}
                                                                order={order}
                                                                orderId={orderId}
                                                                returns={returns}
                                                                refundTransactionsService={refundTransactionsService}
                                                                refundTransactions={refundTransactions?.filter((refundTransaction) => refundTransaction?.orderReturn?.id === r?.id)}
                                                                reloadData={reloadData}
                                                            />
                                                        </div>
                                                    </TurnrGrid>
                                                );
                                            })
                                    )}
                                </TurnrGrid>
                            </ReturnCardsWrapper>
                            <OrderCardsWrapper onlyOneReturnCard={onlyOneReturnCard}>
                                <div className={styles["right-side-order-stuff"]}>
                                    <TurnrGrid container spacing={1} direction={"row"}>
                                        {manualRefundTransactions?.length > 0 &&
                                            manualRefundTransactions?.map((refundTransaction, index) => {
                                                return (
                                                    <TurnrGrid item xs={12} key={index}>
                                                        <ReturnRefundCard
                                                            appProps={appProps}
                                                            index={index}
                                                            onUpdate={reloadData}
                                                            nrOfRefunds={manualRefundTransactions?.length}
                                                            refundTransaction={refundTransaction}
                                                        />
                                                    </TurnrGrid>
                                                );
                                            })}
                                        <TurnrGrid item xs={12}>
                                            <TurnrCard loading={loading} style={{ borderRadius: 0 }}>
                                                <TurnrCardHeader
                                                    title={"Order"}
                                                    dotsMenu={
                                                        <DotsMenu
                                                            items={[
                                                                {
                                                                    label: "Create return",
                                                                    onClick: () => setCreateReturnModalOpen(true),
                                                                },
                                                            ]}
                                                        />
                                                    }
                                                />
                                                <TurnrCardContent>
                                                    <OrderInformationInfoBox appProps={appProps} size={"small"} order={order} />
                                                </TurnrCardContent>
                                                {orderErrorChip && (
                                                    <TurnrCardContent>
                                                        <div style={{ width: "fit-content" }}>{orderErrorChip}</div>
                                                    </TurnrCardContent>
                                                )}
                                            </TurnrCard>
                                            <OrderNotesCard appProps={appProps} orderId={orderId} />
                                        </TurnrGrid>

                                        <TurnrGrid item xs={12}>
                                            <TurnrCard loading={loading} style={{ borderRadius: 0 }}>
                                                <TurnrCardHeader
                                                    title={"Customer"}
                                                    dotsMenu={
                                                        order?.customer && (
                                                            <EntityActionsDotsMenu
                                                                appProps={appProps}
                                                                entity={order?.customer}
                                                                entityType={ENTITY_TYPE.CUSTOMER}
                                                                fetchData={{ entityType: ENTITY_TYPE.CUSTOMER, entityId: order?.customer?.id }}
                                                                hideIfEmpty
                                                                onSuccess={reloadData}
                                                            />
                                                        )
                                                    }
                                                />
                                                <TurnrCardContent>
                                                    <EntityAddressInfoBox size={"small"} entityAddress={order?.entityAddress} customer={order?.customer} />
                                                    <CustomerTagsChips customer={order?.customer} style={{ marginTop: "10px" }} />
                                                </TurnrCardContent>
                                                <LineDivider />
                                                <TurnrCardContent>
                                                    <ReturnStatisticsCard customer={order?.customer} showValue={false} />
                                                </TurnrCardContent>
                                            </TurnrCard>
                                        </TurnrGrid>
                                    </TurnrGrid>
                                </div>
                            </OrderCardsWrapper>
                            {isRoleSuperAdmin(appProps) && !loading && (
                                <ReturnCardsWrapper onlyOneReturnCard={onlyOneReturnCard}>
                                    <TurnrGrid item xs={12}>
                                        <TurnrCard>
                                            <TurnrCardHeader title={"Order as Json"} />
                                            <TurnrCardContent>
                                                <ObjectAsJson
                                                    name="orderData"
                                                    level={2}
                                                    src={{
                                                        order: orderService?.data,
                                                        returns: returnsService?.data,
                                                        refundTransactions: refundTransactionsService?.data,
                                                        warehouseReturns: warehouseReturnsService?.data,
                                                    }}
                                                />
                                            </TurnrCardContent>
                                        </TurnrCard>
                                    </TurnrGrid>
                                </ReturnCardsWrapper>
                            )}
                        </>
                    )}
                </TurnrGrid>
            </PageWrapper>
        </>
    );
}
